'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { ChargeCategorie } from '@/lib/types'
import { formatDT, formatTime, getTodayDate } from '@/lib/utils'
import { ArrowLeft, Zap, Home, Truck, Users, MoreHorizontal, X, Wallet, TrendingDown, ShoppingBag, ChevronDown, ChevronUp, UserCheck, Lock, LockOpen } from 'lucide-react'
import Link from 'next/link'

const CHARGE_ICONS_BASE: Record<ChargeCategorie, { icon: any; color: string }> = {
  kre7:      { icon: Home,           color: 'bg-blue-100 text-blue-600' },
  steg:      { icon: Zap,            color: 'bg-yellow-100 text-yellow-600' },
  sonede:    { icon: Zap,            color: 'bg-cyan-100 text-cyan-600' },
  salaire:   { icon: Users,          color: 'bg-purple-100 text-purple-600' },
  transport: { icon: Truck,          color: 'bg-orange-100 text-orange-600' },
  autre:     { icon: MoreHorizontal, color: 'bg-gray-100 text-gray-600' },
}

type Modal = 'retrait' | 'charge' | 'open_caisse' | 'fermer' | null

export default function CaissePage() {
  const { session } = useApp()
  const { t } = useLang()
  const router = useRouter()

  const CHARGE_ICONS: Record<ChargeCategorie, { icon: any; label: string; color: string }> = {
    kre7:      { ...CHARGE_ICONS_BASE.kre7,      label: t('kre7') },
    steg:      { ...CHARGE_ICONS_BASE.steg,      label: t('steg') },
    sonede:    { ...CHARGE_ICONS_BASE.sonede,    label: t('sonede') },
    salaire:   { ...CHARGE_ICONS_BASE.salaire,   label: t('salaire') },
    transport: { ...CHARGE_ICONS_BASE.transport, label: t('transport') },
    autre:     { ...CHARGE_ICONS_BASE.autre,     label: t('autre') },
  }

  const [data, setData] = useState<any>(null)
  const [ventes, setVentes] = useState<any[]>([])
  const [expandedVente, setExpandedVente] = useState<string | null>(null)
  const [paiementsClients, setPaiementsClients] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<Modal>(null)
  const [retraitForm, setRetraitForm] = useState({ montant: '', note: '' })
  const [chargeForm, setChargeForm] = useState<{ categorie: ChargeCategorie; montant: string; note: string }>({ categorie: 'kre7', montant: '', note: '' })
  const [openForm, setOpenForm] = useState({ solde_ouverture: '' })
  const [soldeReel, setSoldeReel] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    load()
  }, [session])

  async function load() {
    setLoading(true)
    const today = getTodayDate()
    const shopId = session!.shop.id
    const [d, v] = await Promise.all([
      api.getCaisse(shopId, today),
      fetch(`/api/caisse/ventes?shop_id=${shopId}&date=${today}`).then(r => r.json()),
    ])
    setData(d)
    setVentes(v || [])
    setPaiementsClients(d?.paiements_clients || [])
    setLoading(false)
  }

  async function rouvrirCaisse() {
    if (!data?.session?.id) return
    setSaving(true)
    await fetch('/api/caisse/fermer', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ caisse_session_id: data.session.id }),
    })
    setSaving(false)
    load()
  }

  async function fermerCaisse() {
    setSaving(true)
    await fetch('/api/caisse/fermer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        caisse_session_id: data?.session?.id,
        solde_reel: soldeReel ? parseFloat(soldeReel) : null,
      }),
    })
    setModal(null)
    setSoldeReel('')
    setSaving(false)
    load()
  }

  async function openCaisse() {
    setSaving(true)
    await api.openCaisse(session!.shop.id, session!.user.id, parseFloat(openForm.solde_ouverture) || 0)
    setModal(null); setSaving(false); load()
  }

  async function addRetrait() {
    if (!retraitForm.montant || !retraitForm.note) return
    setSaving(true)
    await api.addRetrait({
      shop_id: session!.shop.id,
      user_id: session!.user.id,
      caisse_session_id: data?.session?.id || null,
      montant: parseFloat(retraitForm.montant),
      note: retraitForm.note,
    })
    setRetraitForm({ montant: '', note: '' })
    setModal(null); setSaving(false); load()
  }

  async function addCharge() {
    if (!chargeForm.montant) return
    setSaving(true)
    await api.addCharge({
      shop_id: session!.shop.id,
      user_id: session!.user.id,
      caisse_session_id: data?.session?.id || null,
      categorie: chargeForm.categorie,
      montant: parseFloat(chargeForm.montant),
      note: chargeForm.note || null,
      date: getTodayDate(),
    })
    setChargeForm({ categorie: 'kre7', montant: '', note: '' })
    setModal(null); setSaving(false); load()
  }

  const session_ = data?.session
  // Utiliser solde_theorique calculé par l'API (inclut paiements clients)
  const soldeTh = session_ ? parseFloat(session_.solde_theorique) : null

  return (
    <div className="min-h-screen pb-6">
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/dashboard" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1">{t("caisse_title")}</h1>
          {session_?.ferme_a ? (
            <button onClick={rouvrirCaisse} disabled={saving}
              className="flex items-center gap-1.5 text-sm font-bold text-green-600 bg-green-50 px-3 py-2 rounded-xl border border-green-200 active:scale-95 disabled:opacity-50">
              <LockOpen className="w-4 h-4" /> Rouvrir
            </button>
          ) : session_ ? (
            <button onClick={() => setModal('fermer')}
              className="flex items-center gap-1.5 text-sm font-bold text-orange-600 bg-orange-50 px-3 py-2 rounded-xl border border-orange-200 active:scale-95">
              <Lock className="w-4 h-4" /> {t("close_caisse").replace("🔒 ","")}
            </button>
          ) : (
            <span className="text-sm text-gray-400">{new Date().toLocaleDateString('fr-TN')}</span>
          )}
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : !session_ ? (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl p-6 shadow-sm text-center">
              <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">{t("caisse_closed")}</p>
              <button onClick={() => setModal('open_caisse')} className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg active:scale-95">
                Ouvrir la caisse
              </button>
            </div>
            {/* Paiements clients même sans caisse ouverte */}
            {paiementsClients.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-teal-500" />
                    <h3 className="font-bold text-gray-800">{t("client_payments")}</h3>
                  </div>
                  <span className="font-bold text-teal-600">
                    +{formatDT(paiementsClients.reduce((s: number, p: any) => s + parseFloat(p.montant), 0))}
                  </span>
                </div>
                {paiementsClients.map((p: any) => (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
                    <div className="w-9 h-9 bg-teal-100 rounded-xl flex items-center justify-center">
                      <UserCheck className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{p.client_name}</p>
                      <p className="text-xs text-gray-400">{formatTime(p.created_at)}</p>
                    </div>
                    <span className="font-bold text-teal-600">+{formatDT(p.montant)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className={`rounded-3xl p-5 text-white ${session_?.ferme_a ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-indigo-600 to-indigo-700'}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-indigo-200 text-sm">{t("theoretical_balance")}</p>
                {session_?.ferme_a && (
                  <span className="flex items-center gap-1 text-xs bg-white/20 px-2 py-1 rounded-lg">
                    <Lock className="w-3 h-3" /> Fermée à {formatTime(session_.ferme_a)}
                  </span>
                )}
              </div>
              <p className="text-4xl font-bold mb-1">{formatDT(soldeTh!)}</p>
              {/* Trace ouverture */}
              <p className="text-white/60 text-xs mb-2">
                Ouvert avec {formatDT(parseFloat(session_.solde_ouverture))}
                {session_?.created_at && ` à ${formatTime(session_.created_at)}`}
              </p>
              {session_?.solde_reel != null && (
                <p className="text-sm text-white/70 mb-3">
                  Solde réel compté: <strong className="text-white">{formatDT(parseFloat(session_.solde_reel))}</strong>
                  {Math.abs(parseFloat(session_.solde_reel) - soldeTh!) > 0.001 && (
                    <span className="ml-1 text-yellow-300">
                      (écart: {formatDT(parseFloat(session_.solde_reel) - soldeTh!)})
                    </span>
                  )}
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Ventes',    val: session_.total_ventes,             color: 'text-green-300' },
                  { label: 'Clients',   val: session_.total_paiements_clients,  color: 'text-teal-300' },
                  { label: 'Retraits',  val: session_.total_retraits,           color: 'text-red-300' },
                  { label: 'Charges',   val: session_.total_charges,            color: 'text-orange-300' },
                ].map(s => (
                  <div key={s.label} className="bg-white/10 rounded-2xl p-2 text-center">
                    <p className={`font-bold text-sm ${s.color}`}>{formatDT(parseFloat(s.val || 0))}</p>
                    <p className="text-indigo-200 text-xs">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={`grid grid-cols-2 gap-3 ${session_?.ferme_a ? 'opacity-40 pointer-events-none' : ''}`}>
              <button onClick={() => setModal('retrait')} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 active:scale-95">
                <div className="w-11 h-11 bg-red-100 rounded-xl flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-800">{t("add_withdrawal")}</p>
                  <p className="text-xs text-gray-400">{t("withdrawals")}</p>
                </div>
              </button>
              <button onClick={() => setModal('charge')} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3 active:scale-95">
                <div className="w-11 h-11 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-orange-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-800">{t("add_charge")}</p>
                  <p className="text-xs text-gray-400">STEG, kré7...</p>
                </div>
              </button>
            </div>

            {data?.retraits?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <p className="px-4 py-3 font-bold text-gray-800 border-b border-gray-100">{t("day_withdrawals")}</p>
                {data.retraits.map((r: any) => (
                  <div key={r.id} className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="font-medium text-gray-800">{r.note}</p>
                      <p className="text-xs text-gray-400">{formatTime(r.created_at)}</p>
                    </div>
                    <span className="font-bold text-red-500">-{formatDT(r.montant)}</span>
                  </div>
                ))}
              </div>
            )}

            {data?.charges?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <p className="px-4 py-3 font-bold text-gray-800 border-b border-gray-100">{t("day_charges")}</p>
                {data.charges.map((c: any) => {
                  const cfg = CHARGE_ICONS[c.categorie as ChargeCategorie]
                  const Icon = cfg.icon
                  return (
                    <div key={c.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${cfg.color}`}><Icon className="w-4 h-4" /></div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">{cfg.label}</p>
                        {c.note && <p className="text-xs text-gray-400">{c.note}</p>}
                      </div>
                      <span className="font-bold text-orange-500">-{formatDT(c.montant)}</span>
                    </div>
                  )
                })}
              </div>
            )}
            {/* Paiements clients du jour */}
            {paiementsClients.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-teal-500" />
                    <h3 className="font-bold text-gray-800">{t("client_payments")}</h3>
                  </div>
                  <span className="font-bold text-teal-600">
                    +{formatDT(paiementsClients.reduce((s: number, p: any) => s + parseFloat(p.montant), 0))}
                  </span>
                </div>
                {paiementsClients.map((p: any) => (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
                    <div className="w-9 h-9 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <UserCheck className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800">{p.client_name}</p>
                      {p.note && <p className="text-xs text-gray-400">{p.note}</p>}
                      <p className="text-xs text-gray-400">{formatTime(p.created_at)}</p>
                    </div>
                    <span className="font-bold text-teal-600">+{formatDT(p.montant)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Ventes du jour */}
            {ventes.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-green-500" />
                    <h3 className="font-bold text-gray-800">{t("day_sales")}</h3>
                  </div>
                  <span className="text-sm text-gray-500 font-medium">{ventes.length} vente{ventes.length > 1 ? 's' : ''}</span>
                </div>
                {ventes.map((vente: any, idx: number) => (
                  <div key={vente.id} className="border-b border-gray-50 last:border-0">
                    <button
                      onClick={() => setExpandedVente(expandedVente === vente.id ? null : vente.id)}
                      className="w-full flex items-center justify-between px-4 py-3"
                    >
                      <div className="text-left">
                        <p className="font-semibold text-gray-800">
                          Vente #{ventes.length - idx} — {vente.vendeur_name}
                        </p>
                        <p className="text-xs text-gray-400">{formatTime(vente.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <span className="font-bold text-green-600">{formatDT(vente.montant_encaisse ?? vente.total)}</span>
                          {vente.montant_encaisse != null && parseFloat(vente.montant_encaisse) < parseFloat(vente.total) && (
                            <p className="text-xs text-orange-500">
                              kardé {formatDT(parseFloat(vente.total) - parseFloat(vente.montant_encaisse))}
                            </p>
                          )}
                        </div>
                        {expandedVente === vente.id
                          ? <ChevronUp className="w-4 h-4 text-gray-400" />
                          : <ChevronDown className="w-4 h-4 text-gray-400" />
                        }
                      </div>
                    </button>
                    {expandedVente === vente.id && (
                      <div className="px-4 pb-3 space-y-1.5">
                        {vente.lignes?.map((l: any, i: number) => (
                          <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                            <div>
                              <span className="text-sm font-medium text-gray-700">{l.article_name}</span>
                              <span className="text-xs text-gray-400 ml-2">× {l.quantite} {l.unit}</span>
                            </div>
                            <span className="text-sm font-bold text-gray-800">
                              {formatDT(l.prix_unitaire * l.quantite)}
                            </span>
                          </div>
                        ))}
                        <div className="pt-1 border-t border-gray-200 mt-2 space-y-1">
                          {vente.montant_encaisse != null && parseFloat(vente.montant_encaisse) < parseFloat(vente.total) && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Total vente</span>
                                <span className="font-semibold text-gray-700">{formatDT(vente.total)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-orange-500">Kardé (crédit)</span>
                                <span className="font-semibold text-orange-500">-{formatDT(parseFloat(vente.total) - parseFloat(vente.montant_encaisse))}</span>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between">
                            <span className="text-sm font-semibold text-gray-600">{t("encaisse")}</span>
                            <span className="font-bold text-green-600">{formatDT(vente.montant_encaisse ?? vente.total)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {modal === 'open_caisse' && (
        <ModalWrapper onClose={() => setModal(null)} title="Ouvrir la caisse">
          <p className="text-gray-500 text-sm mb-4">Combien 3andek fel caissa mantali9?</p>
          <div className="relative mb-6">
            <input type="number" inputMode="decimal" autoFocus value={openForm.solde_ouverture}
              onChange={e => setOpenForm({ solde_ouverture: e.target.value })} placeholder="0.000"
              className="w-full px-4 py-3.5 pr-12 text-xl font-bold rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-indigo-500" />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">DT</span>
          </div>
          <button onClick={openCaisse} disabled={saving} className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50">
            {saving ? '...' : 'Ouvrir ✓'}
          </button>
        </ModalWrapper>
      )}

      {modal === 'retrait' && (
        <ModalWrapper onClose={() => setModal(null)} title="Retrait caisse">
          <div className="space-y-3 mb-6">
            <div className="relative">
              <input type="number" inputMode="decimal" autoFocus value={retraitForm.montant}
                onChange={e => setRetraitForm(f => ({ ...f, montant: e.target.value }))} placeholder="Montant..."
                className="w-full px-4 py-3.5 pr-12 text-xl font-bold rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">DT</span>
            </div>
            <input value={retraitForm.note} onChange={e => setRetraitForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Pour quoi? (kré7, achat...)"
              className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
          </div>
          <button onClick={addRetrait} disabled={saving || !retraitForm.montant || !retraitForm.note}
            className="w-full py-4 bg-red-500 text-white rounded-2xl font-bold text-lg disabled:opacity-50">
            {saving ? '...' : 'Retirer ✓'}
          </button>
        </ModalWrapper>
      )}

      {modal === 'charge' && (
        <ModalWrapper onClose={() => setModal(null)} title="Ajouter charge">
          <div className="space-y-3 mb-6">
            <div className="grid grid-cols-3 gap-2">
              {(Object.entries(CHARGE_ICONS) as [ChargeCategorie, any][]).map(([key, cfg]) => {
                const Icon = cfg.icon
                return (
                  <button key={key} onClick={() => setChargeForm(f => ({ ...f, categorie: key }))}
                    className={`py-2.5 rounded-2xl text-xs font-semibold border-2 flex flex-col items-center gap-1 ${chargeForm.categorie === key ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600'}`}>
                    <Icon className="w-4 h-4" />{cfg.label}
                  </button>
                )
              })}
            </div>
            <div className="relative">
              <input type="number" inputMode="decimal" autoFocus value={chargeForm.montant}
                onChange={e => setChargeForm(f => ({ ...f, montant: e.target.value }))} placeholder="Montant..."
                className="w-full px-4 py-3.5 pr-12 text-xl font-bold rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">DT</span>
            </div>
            <input value={chargeForm.note} onChange={e => setChargeForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Note (optionnel)..."
              className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
          </div>
          <button onClick={addCharge} disabled={saving || !chargeForm.montant}
            className="w-full py-4 bg-orange-500 text-white rounded-2xl font-bold text-lg disabled:opacity-50">
            {saving ? '...' : 'Enregistrer ✓'}
          </button>
        </ModalWrapper>
      )}

      {modal === 'fermer' && soldeTh !== null && (
        <ModalWrapper onClose={() => setModal(null)} title="🔒 Fermer la caisse">
          {/* Récap du jour */}
          <div className="bg-gray-50 rounded-2xl p-4 mb-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Ouverture</span>
              <span className="font-semibold">{formatDT(parseFloat(session_?.solde_ouverture || 0))}</span>
            </div>
            <div className="flex justify-between text-green-600">
              <span>+ Ventes encaissées</span>
              <span className="font-semibold">{formatDT(parseFloat(session_?.total_ventes || 0))}</span>
            </div>
            {parseFloat(session_?.total_paiements_clients || 0) > 0 && (
              <div className="flex justify-between text-teal-600">
                <span>+ Paiements clients</span>
                <span className="font-semibold">{formatDT(parseFloat(session_?.total_paiements_clients || 0))}</span>
              </div>
            )}
            {parseFloat(session_?.total_retraits || 0) > 0 && (
              <div className="flex justify-between text-red-500">
                <span>- Retraits</span>
                <span className="font-semibold">{formatDT(parseFloat(session_?.total_retraits || 0))}</span>
              </div>
            )}
            {parseFloat(session_?.total_charges || 0) > 0 && (
              <div className="flex justify-between text-orange-500">
                <span>- Charges</span>
                <span className="font-semibold">{formatDT(parseFloat(session_?.total_charges || 0))}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base border-t border-gray-200 pt-2 mt-1">
              <span>Solde théorique</span>
              <span className="text-indigo-700">{formatDT(soldeTh)}</span>
            </div>
          </div>

          {/* Solde réel compté */}
          <p className="text-sm text-gray-600 mb-2">Combien tu comptes réellement dans la caisse ?</p>
          <div className="relative mb-5">
            <input
              type="number" inputMode="decimal" autoFocus
              value={soldeReel}
              onChange={e => setSoldeReel(e.target.value)}
              placeholder={formatDT(soldeTh)}
              className="w-full px-4 py-3.5 pr-12 text-xl font-bold rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:bg-white focus:border-orange-400"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">DT</span>
          </div>

          {soldeReel && Math.abs(parseFloat(soldeReel) - soldeTh) > 0.001 && (
            <div className={`rounded-2xl p-3 mb-4 text-sm font-semibold flex justify-between ${parseFloat(soldeReel) > soldeTh ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              <span>Écart</span>
              <span>{parseFloat(soldeReel) > soldeTh ? '+' : ''}{formatDT(parseFloat(soldeReel) - soldeTh)}</span>
            </div>
          )}

          <button onClick={fermerCaisse} disabled={saving}
            className="w-full py-4 bg-gray-800 text-white rounded-2xl font-bold text-lg disabled:opacity-50 active:scale-95 flex items-center justify-center gap-2">
            {saving ? '...' : <><Lock className="w-5 h-5" /> Fermer la caisse</>}
          </button>
        </ModalWrapper>
      )}
    </div>
  )
}

function ModalWrapper({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-3xl p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}
